import React, { useContext, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  Typography,
  Button,
  Avatar,
  Paper,
  IconButton,
  LinearProgress,
  Chip,
  Slider,
  TextField,
  CircularProgress,
  Modal,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { styled, useMediaQuery, useTheme } from "@mui/system";
import { COLORS } from "../utils/COLORS";
import { useParams, useNavigate } from "react-router-dom";
import UploadIcon from "@mui/icons-material/Upload";
import GlobalStateContext from "../context/globalStateContext";
import UserContext from "../context/userContext";
import SubModal from "../components/SubModal";
import AuthModal from "../components/AuthModal";
import AddCardModal from "../components/AddCardModal";
import ClaimFundsModal from "../components/ClaimFundsModal";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { off, onValue, ref, get } from "firebase/database";
import { database, firestore, functions, firebase } from "../firebaseConfig";
import { httpsCallable } from "firebase/functions";
import { calculateCountdown } from "../utils/HELPER_FUNCTIONS";
import { toast } from "react-simple-toasts";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkIcon from "@mui/icons-material/Link";
import FavoriteIcon from "@mui/icons-material/Favorite";
import PersonIcon from "@mui/icons-material/Person";
import ScaleIcon from "@mui/icons-material/Scale";
import WcIcon from "@mui/icons-material/Wc";
import PetsIcon from "@mui/icons-material/Pets";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import HeightIcon from "@mui/icons-material/Height";
import InfoIcon from "@mui/icons-material/Info";
import ShareIcon from "@mui/icons-material/Share";
import IosShareIcon from "@mui/icons-material/IosShare";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import * as amplitude from '@amplitude/analytics-browser';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// PIXEL PERFECT STYLED COMPONENTS
const PageContainer = styled(Box)({
  width: "100vw",
  maxWidth: "100%",
  minHeight: "100vh",
  backgroundColor: "#000000", // EXACT: Pure black background
  color: "#FFFFFF",
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  display: "flex",
  flexDirection: "column",
  overflowX: "hidden",
  "&::-webkit-scrollbar": {
    width: "8px",
    height: "8px",
  },
  "&::-webkit-scrollbar-track": {
    backgroundColor: "#000000",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "#1E293B",
    borderRadius: "4px",
    "&:hover": {
      backgroundColor: "#334155",
    },
  },
  "&::-webkit-scrollbar-corner": {
    backgroundColor: "#000000",
  },
});

const Header = styled(Box)({
  width: "100%",
  padding: "16px 24px",
});

const HeaderContent = styled(Box)({
  maxWidth: "800px",
  margin: "0 auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
});

const Logo = styled(Box)({
  display: "flex",
  alignItems: "center",
  fontSize: "18px",
  fontWeight: "600",
  color: "#FFFFFF",
  gap: "8px",
  cursor: "pointer",
});

const NavItems = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "24px",
});

const NavLink = styled(Typography)({
  fontSize: "14px",
  color: "#9CA3AF",
  cursor: "pointer",
  fontWeight: "400",
  "&:hover": {
    color: "#FFFFFF",
  },
});

const ShareButton = styled(Button)({
  backgroundColor: "transparent",
  color: "#9CA3AF",
  border: "1px solid #374151",
  borderRadius: "6px",
  padding: "8px 12px",
  fontSize: "12px",
  fontWeight: "400",
  textTransform: "none",
  minWidth: "auto",
  "&:hover": {
    backgroundColor: "#1F2937",
    color: "#FFFFFF",
  },
});

const MainContainer = styled(Box)(({ theme }) => ({
  maxWidth: "800px",
  margin: "0 auto",
  padding: "20px 24px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flex: 1,
  [theme.breakpoints.down("md")]: {
    padding: "10px 16px",
    alignItems: "flex-start",
  },
}));

const TopSection = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: "32px",
  marginBottom: "40px",
  alignItems: "center",
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
    gap: "24px",
    marginBottom: "20px",
    alignItems: "center",
  },
}));

const DogPhotoContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  flex: "0 0 400px",
  [theme.breakpoints.down("md")]: {
    flex: "none",
    width: "100%",
    maxWidth: "300px",
  },
}));

const DogPhotoCard = styled(Paper)(({ theme }) => ({
  backgroundColor: "transparent",
  borderRadius: "12px",
  padding: "0px",
  textAlign: "center",
  border: "none",
  position: "relative",
  width: "400px",
  height: "450px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  [theme.breakpoints.down("md")]: {
    width: "100%",
    height: "300px",
  },
}));

const UrgentBadge = styled(Chip)({
  position: "absolute",
  top: "16px",
  left: "16px",
  backgroundColor: "#EF4444",
  color: "#FFFFFF",
  fontSize: "12px",
  fontWeight: "600",
  height: "24px",
  "& .MuiChip-label": {
    padding: "0 8px",
  },
});

const PhotoPlaceholder = styled(Typography)({
  color: "#94A3B8",
  fontSize: "16px",
  fontWeight: "500",
});

const DogInfoSection = styled(Box)({
  flex: 1,
  paddingTop: "24px",
});

const DogName = styled(Typography)(({ theme }) => ({
  fontSize: "48px",
  fontWeight: "700",
  color: "#FFFFFF",
  marginBottom: "4px",
  lineHeight: "1.1",
  [theme.breakpoints.down("md")]: {
    fontSize: "36px",
    textAlign: "center",
  },
}));

const AnimalId = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: "400",
  color: "#6B7280",
  marginBottom: "8px",
  [theme.breakpoints.down("md")]: {
    textAlign: "center",
  },
}));

const CountdownBanner = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  fontWeight: "600",
  color: "#DC2626",
  // marginBottom: "8px",
  animation: "subtleRedBlink 2.5s ease-in-out infinite",
  "@keyframes subtleRedBlink": {
    "0%": {
      color: "#DC2626",
      opacity: 1,
    },
    "50%": {
      color: "#B91C1C",
      opacity: 0.85,
    },
    "100%": {
      color: "#DC2626",
      opacity: 1,
    },
  },
  [theme.breakpoints.down("md")]: {
    textAlign: "center",
  },
}));

const EuthanasiaText = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  color: "#9CA3AF",
  fontWeight: "400",
  display: "-webkit-box",
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  [theme.breakpoints.down("md")]: {
    textAlign: "center",
    fontSize: "14px",
  },
}));

const ExpandedText = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  color: "#9CA3AF",
  marginBottom: "8px",
  fontWeight: "400",
  [theme.breakpoints.down("md")]: {
    textAlign: "center",
    fontSize: "14px",
  },
}));

const ViewMoreButton = styled(Typography)({
  fontSize: "14px",
  color: "#10B981",
  cursor: "pointer",
  fontWeight: "500",
  display: "inline",
  marginLeft: "8px",
  "&:hover": {
    color: "#059669",
    textDecoration: "underline",
  },
});

const StatsContainer = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: "8px",
  marginBottom: "16px",
});

const StatsContainerRow2 = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(3, 1fr)",
  gap: "8px",
});

const StatCard = styled(Paper)({
  backgroundColor: "#1F2937", // EXACT: Dark gray from design
  borderRadius: "8px",
  padding: "8px",
  textAlign: "center",
  flex: 1,
  border: "none",
  minHeight: "60px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
});

const StatIcon = styled(Box)({
  color: "#6B7280",
  marginBottom: "4px",
  display: "flex",
  justifyContent: "center",
});

const StatLabel = styled(Typography)({
  fontSize: "12px",
  color: "#9CA3AF",
  marginBottom: "2px",
  fontWeight: "400",
});

const StatValue = styled(Typography)({
  fontSize: "14px",
  fontWeight: "600",
  color: "#FFFFFF",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
});

const HelpSaveSection = styled(Box)({
  textAlign: "center",
  marginBottom: "80px",
  maxWidth: "600px",
  margin: "0 auto 80px auto",
});

const HelpSaveTitle = styled(Typography)({
  fontSize: "32px",
  fontWeight: "600",
  color: "#FFFFFF",
  marginBottom: "8px",
});

const HelpSaveSubtitle = styled(Typography)({
  fontSize: "16px",
  color: "#9CA3AF",
  marginBottom: "40px",
  fontWeight: "400",
});

const DonationCard = styled(Paper)(({ theme }) => ({
  backgroundColor: "#0F172A",
  borderRadius: "14px",
  padding: "16px 16px 8px 16px",
  border: "1px solid #1E293B",
  width: "95%",
  margin: "20px 0 0 0",
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  transition: "all 0.2s ease",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
  },
  [theme.breakpoints.down("md")]: {
    margin: "20px auto 0 auto",
    maxWidth: "350px",
  },
}));

const ProgressSection = styled(Box)({
  marginBottom: "14px",
});

const ProgressHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: "8px",
});

const ProgressLabel = styled(Typography)({
  fontSize: "12px",
  color: "#9CA3AF",
  fontWeight: "500",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

const ProgressAmount = styled(Typography)({
  fontSize: "22px",
  fontWeight: "700",
  color: "#FFFFFF",
  marginBottom: "8px",
});

const CustomProgressBar = styled(LinearProgress)({
  height: "4px",
  borderRadius: "2px",
  backgroundColor: "#374151",
  "& .MuiLinearProgress-bar": {
    backgroundColor: "#10B981",
    borderRadius: "2px",
  },
  marginBottom: "6px",
});

const PercentageText = styled(Typography)({
  fontSize: "12px",
  color: "#9CA3AF",
  textAlign: "right",
  fontWeight: "500",
});

const ChooseAmountTitle = styled(Typography)({
  fontSize: "16px",
  fontWeight: "600",
  color: "#FFFFFF",
  marginBottom: "24px",
  textAlign: "left",
});

const AmountRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  marginBottom: "12px",
  gap: "16px",
});

const SelectedAmountDisplay = styled(Typography)({
  fontSize: "28px",
  fontWeight: "700",
  color: "#FFFFFF",
  minWidth: "80px",
});

const StyledSlider = styled(Slider)({
  flex: 1,
  color: "#10B981",
  height: 4,
  "& .MuiSlider-track": {
    border: "none",
    backgroundColor: "#10B981",
  },
  "& .MuiSlider-rail": {
    backgroundColor: "#374151",
    opacity: 1,
  },
  "& .MuiSlider-thumb": {
    height: 16,
    width: 16,
    backgroundColor: "#10B981",
    border: "2px solid #FFFFFF",
    boxShadow: "0 2px 8px rgba(16, 185, 129, 0.3)",
    "&:focus, &:hover, &.Mui-active, &.Mui-focusVisible": {
      backgroundColor: COLORS.primaryGreen,
      boxShadow: "0 4px 12px rgba(16, 185, 129, 0.4)",
    },
    "&:before": {
      display: "none",
    },
  },
});

const DonationAmounts = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(5, 1fr)",
  gap: "6px",
  marginBottom: "14px",
});

const AmountButton = styled(Button)(({ selected }) => ({
  backgroundColor: selected ? "#10B981" : "#1E293B",
  color: "#FFFFFF",
  border: selected ? "1px solid #10B981" : "1px solid #334155",
  borderRadius: "8px",
  padding: "8px 6px",
  fontSize: "13px",
  fontWeight: "500",
  textTransform: "none",
  minWidth: "auto",
  transition: "all 0.15s ease",
  "&:hover": {
    backgroundColor: selected ? "#059669" : "#334155",
    transform: "translateY(-1px)",
  },
}));

const AmountTextField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#1E293B",
    border: "1px solid #334155",
    borderRadius: "8px",
    height: "100%",
    color: "#FFFFFF",
    fontSize: "13px",
    "& fieldset": {
      border: "none",
    },
    "&:hover": {
      backgroundColor: "#334155",
    },
    "&.Mui-focused": {
      backgroundColor: "#334155",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "8px 6px",
    textAlign: "center",
    fontSize: "13px",
    fontWeight: "500",
    color: "#FFFFFF",
    "&::placeholder": {
      color: "#9CA3AF",
      opacity: 1,
    },
  },
});

const DonateButton = styled(Button)({
  width: "100%",
  backgroundColor: "#10B981",
  color: "#FFFFFF",
  borderRadius: "10px",
  padding: "12px",
  marginBottom: "14px",
  fontSize: "15px",
  fontWeight: "600",
  textTransform: "none",
  boxShadow: "0 2px 8px rgba(16, 185, 129, 0.25)",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: "#059669",
    transform: "translateY(-1px)",
    boxShadow: "0 4px 12px rgba(16, 185, 129, 0.35)",
  },
});

const StorySection = styled(Paper)({
  backgroundColor: "#1F2937", // EXACT: Dark gray from design
  borderRadius: "12px",
  padding: "32px",
  border: "none",
  marginBottom: "80px",
});

const StoryTitle = styled(Typography)({
  fontSize: "24px",
  fontWeight: "600",
  color: "#FFFFFF",
  marginBottom: "24px",
  textAlign: "center",
});

const StoryText = styled(Typography)({
  fontSize: "16px",
  lineHeight: "1.6",
  color: "#D1D5DB",
  marginBottom: "16px",
  fontWeight: "400",
});

const ShareSection = styled(Box)({
  textAlign: "center",
  marginBottom: "80px",
});

const ShareTitle = styled(Typography)({
  fontSize: "24px",
  fontWeight: "600",
  color: "#FFFFFF",
  marginBottom: "8px",
});

const ShareSubtitle = styled(Typography)({
  fontSize: "16px",
  color: "#9CA3AF",
  marginBottom: "32px",
  fontWeight: "400",
});

const SocialButtons = styled(Box)({
  display: "flex",
  justifyContent: "center",
  gap: "12px",
});

const SocialButton = styled(IconButton)({
  backgroundColor: "#374151",
  border: "none",
  borderRadius: "8px",
  padding: "12px",
  color: "#D1D5DB",
  "&:hover": {
    backgroundColor: "#4B5563",
    color: "#FFFFFF",
  },
});

const Footer = styled(Box)(({ theme }) => ({
  textAlign: "center",
  padding: "16px 24px",
  color: "#6B7280",
  fontSize: "14px",
  maxWidth: "800px",
  margin: "0 auto",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "8px",
  [theme.breakpoints.down("md")]: {
    gap: "4px",
    padding: "12px 16px",
  },
}));

const FooterLink = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  color: "#9CA3AF",
  cursor: "pointer",
  fontWeight: "400",
  "&:hover": {
    color: "#FFFFFF",
  },
  [theme.breakpoints.down("md")]: {
    fontSize: "12px",
    textAlign: "center",
  },
}));

const Bullet = styled(Typography)(({ theme }) => ({
  fontSize: "14px",
  color: "#6B7280",
  userSelect: "none",
  [theme.breakpoints.down("md")]: {
    fontSize: "12px",
  },
}));

const NavigationArrow = styled(IconButton)(({ theme, position }) => ({
  position: "fixed",
  top: "50%",
  transform: "translateY(-50%)",
  [position]: "24px",
  width: "56px",
  height: "56px",
  backgroundColor: "rgba(0, 0, 0, 0.4)",
  color: "#FFFFFF",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  backdropFilter: "blur(8px)",
  zIndex: 1000,
  opacity: 0.7,
  transition: "all 0.3s ease",
  "&:hover": {
    opacity: 1,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    transform: "translateY(-50%) scale(1.1)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
  },
  "&:disabled": {
    opacity: 0.3,
    cursor: "not-allowed",
  },
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const SwipeIndicator = styled(Box)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.down("md")]: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "12px",
    gap: "4px",
  },
}));

const SwipeDot = styled(Box)(({ active }) => ({
  width: "6px",
  height: "6px",
  borderRadius: "50%",
  backgroundColor: active ? "#10B981" : "#374151",
  transition: "all 0.3s ease",
}));

const SwipeHint = styled(Typography)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.down("md")]: {
    display: "block",
    fontSize: "12px",
    color: "#6B7280",
    textAlign: "center",
    marginTop: "8px",
    opacity: 0.7,
  },
}));

const ClaimSuccessMessage = styled(Typography)({
  fontSize: "14px",
  color: "#10B981",
  fontWeight: "500",
  textAlign: "center",
  marginTop: "8px",
  padding: "8px 12px",
  backgroundColor: "rgba(16, 185, 129, 0.1)",
  borderRadius: "6px",
  border: "1px solid rgba(16, 185, 129, 0.2)",
});

const MediaNavigationDots = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "6px",
  marginTop: "12px",
});

const MediaDot = styled(Button)(({ active }) => ({
  minWidth: "8px",
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  padding: "0",
  backgroundColor: active ? "#10B981" : "#374151",
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: active ? "#059669" : "#4B5563",
    transform: "scale(1.2)",
  },
}));

const DogProfilePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { globalState, setGlobalState } = useContext(GlobalStateContext);
  const [dogProfile, setDogProfile] = useState(null);
  const [totalPledged, setTotalPledged] = useState(0);
  const [totalSubscriptions, setTotalSubscriptions] = useState(0);
  const [selectedAmount, setSelectedAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState("");
  const [showMore, setShowMore] = useState(false);
  const [isProcessingPledge, setIsProcessingPledge] = useState(false);
  const [allDogs, setAllDogs] = useState([]);
  const [currentDogIndex, setCurrentDogIndex] = useState(-1);
  const [nextDog, setNextDog] = useState(null);
  const [prevDog, setPrevDog] = useState(null);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isSwipeDragging, setIsSwipeDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [keepPledgeFastFree, setKeepPledgeFastFree] = useState(true);
  const [hasPendingClaim, setHasPendingClaim] = useState(false);
  const [isRescued, setIsRescued] = useState(false);
  const [rescueDate, setRescueDate] = useState(null);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const donationAmounts = [10, 25, 100, 250];
  const fundingGoal = 2500;
  const fundingPercentage = Math.min((totalPledged / fundingGoal) * 100, 100);

  const calcAmount = (percentage) => {
    const fee = Math.round(selectedAmount * (percentage / 100));
    return fee;
  };

  const getAllMedia = () => {
    if (!dogProfile) return [];
    const videos = dogProfile.videos || [];
    const images = dogProfile.images || [];
    return [
      ...videos.map(url => ({ type: 'video', url })),
      ...images.map(url => ({ type: 'image', url }))
    ];
  };

  const allMedia = getAllMedia();
  const currentMedia = allMedia[currentMediaIndex];

  useEffect(() => {
    const fetchAllDogs = async () => {
      try {
        const querySnapshot = await firestore
          .collection("web-available-dogs")
          .get();
        const dogs = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        console.log('---', dogs)
        setAllDogs(dogs);

        const currentIndex = dogs.findIndex((dog) => dog.slug === slug);
        setCurrentDogIndex(currentIndex);

        if (currentIndex >= 0) {
          const currentDog = dogs[currentIndex];

          if (currentDog.shelterId) {
            try {
              const shelterDoc = await firestore
                .collection("availableShelters")
                .doc(currentDog.shelterId)
                .get();
              if (shelterDoc.exists) {
                currentDog.shelter = {
                  id: shelterDoc.id,
                  ...shelterDoc.data(),
                };
              }
            } catch (error) {
              console.error("Error fetching shelter data:", error);
            }
          }

          setDogProfile(currentDog);

          // Prefetch adjacent dogs
          const next = dogs[currentIndex + 1] || null;
          const prev = dogs[currentIndex - 1] || null;
          setNextDog(next);
          setPrevDog(prev);
        } else {
          setDogProfile(null);
        }
      } catch (error) {
        console.error("Error fetching dogs:", error);
        setDogProfile(null);
      }
    };

    fetchAllDogs();
  }, [slug]);

  useEffect(() => {
    if (!dogProfile) return;

    // Fetch pledges from Firestore pledges collection filtered by slug
    const pledgesRef = firestore.collection(`pledges/${slug}/pledges`);
    const pledgesUnsubscribe = pledgesRef.onSnapshot(
      (snapshot) => {
        try {
          if (!snapshot.empty) {
            // Get pledges for this dog (already filtered by the subcollection path)
            const dogPledges = snapshot.docs
              .filter((doc) => doc.exists)
              .map((doc) => ({ id: doc.id, ...doc.data() }));
            const total = dogPledges.reduce(
              (sum, pledge) => sum + (pledge.displayAmount || 0),
              0
            );
            setTotalPledged(total);
            
            // Check for rescue date from processed pledges
            const processedPledge = dogPledges.find(pledge => pledge.status === "processed" && pledge.rescueDate);
            if (processedPledge && processedPledge.rescueDate) {
              setRescueDate(processedPledge.rescueDate);
            }
          } else {
            setTotalPledged(0);
          }
        } catch (error) {
          console.error("Error processing pledges:", error);
          setTotalPledged(0);
        }
      },
      (error) => {
        console.error("Error listening to pledges:", error);
        setTotalPledged(0);
      }
    );

    // Fetch subscriptions from Firestore subscriptions collection filtered by slug
    const subscriptionsRef = firestore
      .collection("subscriptions")
      .where("dogSlug", "==", slug);
    const subscriptionsUnsubscribe = subscriptionsRef.onSnapshot(
      (snapshot) => {
        try {
          if (!snapshot.empty) {
            // Get subscriptions for this dog (already filtered by the where query)
            const dogSubscriptions = snapshot.docs
              .filter((doc) => doc.exists)
              .map((doc) => ({ id: doc.id, ...doc.data() }));
            const total = dogSubscriptions.reduce(
              (sum, subscription) => sum + (subscription.amount || 0),
              0
            );
            setTotalSubscriptions(total);
          } else {
            setTotalSubscriptions(0);
          }
        } catch (error) {
          console.error("Error processing subscriptions:", error);
          setTotalSubscriptions(0);
        }
      },
      (error) => {
        console.error("Error listening to subscriptions:", error);
        setTotalSubscriptions(0);
      }
    );

    return () => {
      pledgesUnsubscribe();
      subscriptionsUnsubscribe();
    };
  }, [dogProfile]);

  // Check for existing claims for this dog
  useEffect(() => {
    if (!dogProfile) return;

    const unsubscribe = firestore
      .collection("claims")
      .where("slug", "==", dogProfile.slug)
      .where("claimStatus", "==", "pending")
      .onSnapshot((querySnapshot) => {
        setHasPendingClaim(!querySnapshot.empty);
      });

    return () => unsubscribe();
  }, [dogProfile]);

  // Check if dog has been rescued
  useEffect(() => {
    if (!dogProfile) return;

    // Check dog's status in real-time
    const unsubscribe = firestore
      .collection("web-available-dogs")
      .where("slug", "==", dogProfile.slug)
      .onSnapshot((querySnapshot) => {
        if (!querySnapshot.empty) {
          const dogData = querySnapshot.docs[0].data();
          const rescued =
            dogData.euthanasiaStatus === "rescued" ||
            dogData.claimStatus === "accepted";
          setIsRescued(rescued);
          
          // Set rescue date if available
          if (rescued && dogData.rescueDate) {
            setRescueDate(dogData.rescueDate);
          }
        }
      });

    return () => unsubscribe();
  }, [dogProfile]);

  const handleOneClickPledge = async () => {
    // Prevent pledging if dog is already rescued
    if (isRescued) {
      toast("This dog has already been rescued! 🎉");
      return;
    }

    setIsProcessingPledge(true);

    try {
      const createCustomerForDelayedCharge = httpsCallable(
        functions,
        "createCustomerForDelayedCharge"
      );

      const tipAmount = keepPledgeFastFree ? calcAmount(15) : 0;
      const totalAmount = selectedAmount + tipAmount;

      const result = await createCustomerForDelayedCharge({
        email: user.email,
        amount: totalAmount * 100, // Convert to cents
        paymentMethodId: user.stripePaymentMethodId,
        dogSlug: dogProfile?.slug,
        orderDetails: {
          dogName: dogProfile?.name,
          pledgeAmount: selectedAmount,
          tipAmount: tipAmount,
          totalAmount: totalAmount,
        },
      });

      const { success, orderId, orderData } = result.data;

      if (success) {
        console.log("Success", success);
        // Save only to Firestore pledges collection
        await firestore
          .collection(`pledges/${dogProfile.slug}/pledges`)
          .doc(orderId)
          .set({
            ...orderData,
            userId: user.uid,
            userEmail: user.email,
            userName: user.username,
            displayAmount: selectedAmount,
            tipAmount: tipAmount,
            totalAmount: totalAmount,
            createdAt: new Date().toISOString(),
            status: "requires_capture",
            slug: dogProfile?.slug,
            name: dogProfile?.name,
            breed: dogProfile?.breed,
            age: dogProfile?.age,
            animalId: dogProfile?.animalId,
          });

        toast("Pledge successful!");

        // Track pledge event with Amplitude
        amplitude.track('pledge', {
          dogName: dogProfile?.name,
          dogSlug: dogProfile?.slug,
          pledgeAmount: selectedAmount,
          tipAmount: tipAmount,
          totalAmount: totalAmount,
          userId: user.uid,
          userEmail: user.email
        });

        // Open SubModal for subscription upsell after successful pledge
        setGlobalState({
          ...globalState,
          showSubModal: true,
          selectedDog: dogProfile,
        });
      } else {
        throw new Error("Failed to create pledge");
      }
    } catch (err) {
      toast("Pledge failed: " + err.message);
      console.error("Pledge error:", err);
    } finally {
      setIsProcessingPledge(false);
    }
  };

  const handleDonateClick = () => {
    if (!user) {
      setGlobalState({ ...globalState, showAuthModal: true });
    } else if (!user?.stripeCustomerId || !user?.stripePaymentMethodId) {
      setGlobalState({
        ...globalState,
        showAddCardModal: true,
        selectedDog: dogProfile,
      });
    } else {
      // One-click pledge
      handleOneClickPledge();
    }
  };

  const navigateToDog = (targetDog) => {
    if (!targetDog) return;
    navigate(`/${targetDog.slug}`, { replace: true });
  };

  const handlePrevDog = () => {
    if (prevDog) {
      navigateToDog(prevDog);
    }
  };

  const handleNextDog = () => {
    if (nextDog) {
      navigateToDog(nextDog);
    }
  };

  const handleNextMedia = () => {
    if (currentMediaIndex < allMedia.length - 1) {
      setCurrentMediaIndex(currentMediaIndex + 1);
    }
  };

  const handlePrevMedia = () => {
    if (currentMediaIndex > 0) {
      setCurrentMediaIndex(currentMediaIndex - 1);
    }
  };

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsSwipeDragging(true);
    setDragOffset(0);
  };

  const onTouchMove = (e) => {
    if (!touchStart) return;

    const currentTouch = e.targetTouches[0].clientX;
    const diff = currentTouch - touchStart;

    // Limit drag distance to prevent excessive movement
    const maxDrag = 120;
    const limitedDiff = Math.max(-maxDrag, Math.min(maxDrag, diff));

    setTouchEnd(currentTouch);
    setDragOffset(limitedDiff);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setIsSwipeDragging(false);
      setDragOffset(0);
      return;
    }

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    // Reset drag state
    setIsSwipeDragging(false);
    setDragOffset(0);

    if (isLeftSwipe && nextDog) {
      // Haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      handleNextDog();
    }

    if (isRightSwipe && prevDog) {
      // Haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      handlePrevDog();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") {
        handlePrevDog();
      } else if (e.key === "ArrowRight") {
        handleNextDog();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [prevDog, nextDog]);

  const getTimeRemaining = () => {
    const countdown = calculateCountdown(dogProfile);
    if (countdown === "Time expired") return "Time expired";
    if (countdown.includes("d")) {
      const days = countdown.split("d")[0];
      return `${days} DAYS LEFT BEFORE EUTHANASIA`;
    }
    return countdown;
  };

  if (!dogProfile) {
    return (
      <PageContainer>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            gap: "16px",
          }}
        >
          <Typography>Sorry this dog didn't make it</Typography>
          <Button
            onClick={() => navigate("/")}
            sx={{
              backgroundColor: "#10B981",
              color: "#FFFFFF",
              borderRadius: "8px",
              padding: "8px 16px",
              fontSize: "14px",
              fontWeight: "500",
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#059669",
              },
            }}
          >
            Go back home
          </Button>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Navigation Arrows - Desktop Only */}
      <NavigationArrow
        position="left"
        onClick={handlePrevDog}
        disabled={!prevDog}
      >
        <ArrowBackIosIcon sx={{ fontSize: "20px" }} />
      </NavigationArrow>

      <NavigationArrow
        position="right"
        onClick={handleNextDog}
        disabled={!nextDog}
      >
        <ArrowForwardIosIcon sx={{ fontSize: "20px" }} />
      </NavigationArrow>

      {/* Header - PIXEL PERFECT */}
      <Header>
        <HeaderContent>
          <Logo onClick={() => navigate("/")}>
            <PetsIcon />
            PledgeFast
          </Logo>
        </HeaderContent>
      </Header>

      <MainContainer
        onTouchStart={isMobile ? onTouchStart : undefined}
        onTouchMove={isMobile ? onTouchMove : undefined}
        onTouchEnd={isMobile ? onTouchEnd : undefined}
      >
        {/* Top Section - PIXEL PERFECT */}
        <TopSection>
          <DogPhotoContainer>
            <DogPhotoCard
              elevation={0}
              sx={{
                transform: isMobile
                  ? `translateX(${dragOffset}px) rotate(${dragOffset * 0.1}deg)`
                  : "none",
                transition: isSwipeDragging ? "none" : "transform 0.3s ease",
                opacity: isMobile
                  ? Math.max(0.7, 1 - Math.abs(dragOffset) / 200)
                  : 1,
              }}
            >
              {currentMedia ? (
                currentMedia.type === 'video' ? (
                  <video
                    src={currentMedia.url}
                    autoPlay
                    loop
                    muted
                    playsInline
                    controls
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "12px",
                      objectFit: "contain",
                    }}
                  />
                ) : (
                  <Avatar
                    src={currentMedia.url}
                    sx={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "12px",
                      "& img": {
                        objectFit: "contain",
                        borderRadius: "12px",
                      },
                    }}
                  />
                )
              ) : (
                <PhotoPlaceholder>No Media Available</PhotoPlaceholder>
              )}
            </DogPhotoCard>

            {/* Media Navigation Dots */}
            {allMedia.length > 1 && (
              <MediaNavigationDots>
                {allMedia.map((_, index) => (
                  <MediaDot
                    key={index}
                    active={index === currentMediaIndex}
                    onClick={() => setCurrentMediaIndex(index)}
                  />
                ))}
              </MediaNavigationDots>
            )}

            {/* Mobile Swipe Indicators */}
            <SwipeIndicator>
              <SwipeDot active={!!prevDog} />
              <SwipeDot active={true} />
              <SwipeDot active={!!nextDog} />
            </SwipeIndicator>
            <SwipeHint>Swipe to browse more dogs</SwipeHint>
          </DogPhotoContainer>

          <DogInfoSection>
            <CountdownBanner
              sx={{
                color: isRescued ? "#10B981" : "#DC2626",
                animation: isRescued
                  ? "none"
                  : "subtleRedBlink 2.5s ease-in-out infinite",
              }}
            >
              {isRescued ? (
                <>
                  🎉 RESCUED!
                  {rescueDate && (
                    <span style={{ marginLeft: "8px", fontSize: "12px", color: "#9CA3AF" }}>
                      {new Date(rescueDate.toDate ? rescueDate.toDate() : rescueDate).toLocaleDateString()}
                    </span>
                  )}
                </>
              ) : (
                getTimeRemaining()
              )}
            </CountdownBanner>
            {!isRescued && (
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "space-between",
                // marginBottom: "4px",
                [theme.breakpoints.down("md")]: {
                  justifyContent: "center",
                  gap: "8px",
                },
              }}>
                <Typography sx={{
                  fontSize: "14px",
                  padding: 0,
                  margin: 0,
                  color: "#9CA3AF",
                  fontWeight: "500",
                  [theme.breakpoints.down("md")]: {
                    textAlign: "center",
                  },
                }}>
                  5x the chance of rescue by sharing
                </Typography>
                <IconButton 
                  onClick={() => {
                    // Track share event
                    amplitude.track('share', {
                      dogName: dogProfile.name,
                      dogSlug: dogProfile.slug,
                      shareMethod: navigator.share ? 'native_share' : 'copy_link',
                      location: 'dog_profile_quick_share'
                    });
                    
                    // Track general shares event
                    amplitude.track('shares', {
                      dogName: dogProfile.name,
                      dogSlug: dogProfile.slug,
                      shareType: 'quick_share'
                    });

                    if (navigator.share) {
                      navigator.share({
                        title: `Help save ${dogProfile.name}`,
                        text: `${dogProfile.name} needs our help! They're scheduled for euthanasia and need pledges to find a rescue.`,
                        url: window.location.href
                      });
                    } else {
                      navigator.clipboard.writeText(window.location.href);
                      toast("Link copied to clipboard!");
                    }
                  }}
                  sx={{ 
                    color: "#9CA3AF", 
                    padding: "4px",
                    minWidth: "auto",
                    "&:hover": { 
                      color: "#FFFFFF",
                      backgroundColor: "#1F2937" 
                    } 
                  }}
                >
                  <IosShareIcon sx={{ fontSize: "16px" }} />
                </IconButton>
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: "4px",
                [theme.breakpoints.down("md")]: {
                  justifyContent: "center",
                },
              }}
            >
              <DogName sx={{ margin: 0 }}>{dogProfile.name}</DogName>
              {/* <IconButton 
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: `Help save ${dogProfile.name}`,
                      text: `${dogProfile.name} needs our help! They're scheduled for euthanasia and need pledges to find a rescue.`,
                      url: window.location.href
                    });
                  } else {
                    navigator.clipboard.writeText(window.location.href);
                    toast("Link copied to clipboard!");
                  }
                }}
                sx={{ 
                  color: "#9CA3AF", 
                  padding: "8px",
                  "&:hover": { 
                    color: "#FFFFFF",
                    backgroundColor: "#1F2937" 
                  } 
                }}
              >
                <ShareIcon sx={{ fontSize: "20px" }} />
              </IconButton> */}
            </Box>
            <AnimalId>
              #{dogProfile.animalId || dogProfile.id || "A001"} •{" "}
              {dogProfile.shelter?.location
                ? dogProfile.shelter.location
                    .split(", ")
                    .slice(-2)
                    .join(", ")
                    .replace(/\s\d{5}(-\d{4})?$/, "")
                    .toUpperCase()
                : "SAN ANTONIO, TX"}
            </AnimalId>
            <Box sx={{ marginBottom: "12px" }}>
              <EuthanasiaText
                sx={{
                  display: showMore ? "block" : "-webkit-box",
                  WebkitLineClamp: showMore ? "none" : 3,
                  overflow: showMore ? "visible" : "hidden",
                  marginBottom: showMore ? "8px" : "0px",
                }}
              >
                {dogProfile.description ||
                  "Scheduled for euthanasia in " + getTimeRemaining()}
              </EuthanasiaText>
              <ViewMoreButton onClick={() => setShowMore(!showMore)}>
                {showMore ? "view less" : "view more"}
              </ViewMoreButton>
            </Box>

            <AnimatePresence>
              {showMore && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{
                    duration: 0.4,
                    ease: [0.25, 0.46, 0.45, 0.94],
                    opacity: { duration: 0.3 },
                  }}
                  style={{ overflow: "hidden" }}
                >
                  {dogProfile.healthIssues &&
                    dogProfile.healthIssues.length > 0 && (
                      <ExpandedText>
                        <strong>Health Issues:</strong>{" "}
                        {dogProfile.healthIssues.join(", ")}
                      </ExpandedText>
                    )}
                  {dogProfile.evaluation && (
                    <ExpandedText>
                      <strong>Evaluation:</strong> {dogProfile.evaluation}
                    </ExpandedText>
                  )}
                  <ExpandedText>
                    <strong>Spayed/Neutered:</strong>{" "}
                    {dogProfile.spayedNeutered === true ||
                    dogProfile.spayedNeutered === "true" ||
                    dogProfile.spayedNeutered === "Yes"
                      ? "Yes"
                      : "No"}
                  </ExpandedText>
                  <ExpandedText>
                    <strong>Vaccinated:</strong>{" "}
                    {dogProfile.vaccinated === true ||
                    dogProfile.vaccinated === "true" ||
                    dogProfile.vaccinated === "Yes"
                      ? "Yes"
                      : "No"}
                  </ExpandedText>
                  <ExpandedText>
                    <strong>Microchipped:</strong>{" "}
                    {dogProfile.microchipped === true ||
                    dogProfile.microchipped === "true" ||
                    dogProfile.microchipped === "Yes"
                      ? "Yes"
                      : "No"}
                  </ExpandedText>

                  {dogProfile.intakeDate && (
                    <ExpandedText>
                      <strong>Intake Date:</strong>{" "}
                      {new Date(dogProfile.intakeDate).toLocaleDateString(
                        "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )}
                    </ExpandedText>
                  )}
                  {dogProfile.reasonForRisk && (
                    <ExpandedText>
                      <strong>Reason for Risk:</strong>{" "}
                      {dogProfile.reasonForRisk}
                    </ExpandedText>
                  )}
                  {dogProfile.shelter && (
                    <ExpandedText sx={{ marginBottom: "32px" }}>
                      <strong>Shelter:</strong> {dogProfile.shelter.name} -{" "}
                      {dogProfile.shelter.location}
                    </ExpandedText>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <StatsContainer>
              <StatCard elevation={0}>
                <StatIcon>
                  <PersonIcon sx={{ fontSize: "16px" }} />
                </StatIcon>
                <StatLabel>Age</StatLabel>
                <StatValue>{dogProfile.age || "3 years"}</StatValue>
              </StatCard>
              <StatCard elevation={0}>
                <StatIcon>
                  <ScaleIcon sx={{ fontSize: "16px" }} />
                </StatIcon>
                <StatLabel>Weight</StatLabel>
                <StatValue>{dogProfile.weight || "45"} lbs</StatValue>
              </StatCard>
              <StatCard elevation={0}>
                <StatIcon>
                  <WcIcon sx={{ fontSize: "16px" }} />
                </StatIcon>
                <StatLabel>Gender</StatLabel>
                <StatValue>{dogProfile.gender || "Female"}</StatValue>
              </StatCard>
            </StatsContainer>

            <DonationCard elevation={0}>
              <ProgressSection>
                <ProgressHeader>
                  <ProgressLabel>Pledges</ProgressLabel>
                  <ProgressLabel sx={{ color: COLORS.primaryGreen }}>
                    ${totalPledged.toLocaleString() || "0"}
                  </ProgressLabel>
                </ProgressHeader>
                <ProgressHeader sx={{ marginTop: "8px" }}>
                  <ProgressLabel>Subscriptions</ProgressLabel>
                  <Box
                    sx={{ display: "flex", alignItems: "baseline", gap: "2px" }}
                  >
                    <ProgressLabel sx={{ color: COLORS.primaryGreen }}>
                      ${totalSubscriptions.toLocaleString()}
                    </ProgressLabel>
                    <Typography
                      sx={{
                        fontSize: "10px",
                        color: "#6B7280",
                        fontWeight: "400",
                      }}
                    >
                      /mo
                    </Typography>
                  </Box>
                </ProgressHeader>
              </ProgressSection>

              {/* <AmountRow>
              <SelectedAmountDisplay>${selectedAmount}</SelectedAmountDisplay>
              <StyledSlider
                value={selectedAmount}
                min={10}
                max={500}
                onChange={(event, newValue) => {
                  setSelectedAmount(newValue);
                }}
                valueLabelDisplay="off"
              />
            </AmountRow> */}

              <DonationAmounts>
                {donationAmounts.map((amount) => (
                  <AmountButton
                    key={amount}
                    selected={selectedAmount === amount && customAmount === ""}
                    onClick={() => {
                      setSelectedAmount(amount);
                      setCustomAmount("");
                    }}
                  >
                    ${amount}
                  </AmountButton>
                ))}
                <AmountTextField
                  placeholder="Custom"
                  value={customAmount}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    setCustomAmount(value);
                    if (value && parseInt(value) > 0) {
                      setSelectedAmount(parseInt(value));
                    }
                  }}
                  onFocus={() => {
                    if (!customAmount) {
                      setCustomAmount("");
                    }
                  }}
                  autoComplete="off"
                />
              </DonationAmounts>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={keepPledgeFastFree}
                    onChange={(e) => setKeepPledgeFastFree(e.target.checked)}
                    sx={{
                      color: "#9CA3AF",
                      "&.Mui-checked": {
                        color: "#10B981",
                      },
                      padding: "4px",
                    }}
                  />
                }
                label={
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "#9CA3AF",
                      fontWeight: "500",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                    }}
                  >
                    💡 Add ${calcAmount(15)} (15%) to keep PledgeFast free for
                    rescues.
                  </Typography>
                }
                sx={{
                  marginBottom: "12px",
                  marginLeft: "0px",
                  alignItems: "flex-start",
                }}
              />

              <DonateButton
                onClick={handleDonateClick}
                disabled={isProcessingPledge || isRescued}
                startIcon={
                  isProcessingPledge ? null : isRescued ? null : (
                    <FavoriteIcon sx={{ color: "#FFFFFF" }} />
                  )
                }
                sx={{
                  backgroundColor: isRescued ? "#10B981" : "#10B981",
                  "&:hover": {
                    backgroundColor: isRescued ? "#10B981" : "#059669",
                  },
                  "&:disabled": {
                    backgroundColor: isRescued ? "#10B981" : "#374151",
                    color: "#FFFFFF",
                  },
                }}
              >
                {isProcessingPledge ? (
                  <CircularProgress size={20} color="inherit" />
                ) : isRescued ? (
                  "🎉 Rescued Successfully!"
                ) : (
                  "Pledge One-Time"
                )}
              </DonateButton>
            </DonationCard>

            <Typography
              sx={{
                fontSize: "12px",
                color: "#6B7280",
                fontWeight: "400",
                marginTop: "4px",
                textAlign: "center",
              }}
            >
              {/* 🤝 100% of pledges go to rescue org. Charged only when a{" "}
              <strong>Proof of Rescue</strong> is approved. Funds transfer in 1
              day. */}
                  🤝 100% goes to rescue org. You're only charged when a{" "}
              rescue org <strong>Proof of Rescue</strong> is approved. Funds transfer in 1
              day.
            </Typography>
            {/* Upload Proof of Rescue Section */}
            <Box sx={{ marginTop: "24px", textAlign: "center" }}>
              <ShareButton
                startIcon={<UploadIcon sx={{ fontSize: "16px" }} />}
                // disabled={isRescued}
                onClick={() => {
                  if(isRescued) {
                    return
                  }
                  if (!user) {
                    setGlobalState({
                      ...globalState,
                      showAuthModal: true,
                    });
                  } else {
                    setGlobalState({
                      ...globalState,
                      showClaimFundsModal: true,
                      selectedDog: dogProfile,
                    });
                  }
                }}
                sx={{
                  "&:hover": {
                    backgroundColor: "#1F2937",
                    color: "#FFFFFF",
                  },
                }}
              >
                Upload Proof of Rescue
              </ShareButton>
              {hasPendingClaim && !isRescued && (
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#10B981",
                    fontWeight: "500",
                    textAlign: "center",
                    marginTop: "8px",
                  }}
                >
                  🎉 Application Pending Approval 🎉
                </Typography>
              )}
            </Box>
          </DogInfoSection>
        </TopSection>
      </MainContainer>

      {/* Footer - PIXEL PERFECT */}
      <Footer>
        <FooterLink
          onClick={() => {
            if (user) {
              navigate(`/profile/${user.username}`);
            } else {
              setGlobalState({ ...globalState, showAuthModal: true });
            }
          }}
        >
          {user ? `${user.username}` : "Login"}
        </FooterLink>
        <Bullet>•</Bullet>
        <FooterLink
          onClick={() => {
            if (user) {
              navigate("/add-animal");
            } else {
              setGlobalState({ ...globalState, showAuthModal: true });
            }
          }}
        >
          Save Your Shelter Dogs
        </FooterLink>
        <Bullet>•</Bullet>
        <FooterLink
          onClick={() =>
            window.open("www.x.com/yilaktibebu",)
          }
        >
          Contact
        </FooterLink>
      </Footer>

      {/* Modals */}
      <Elements stripe={stripePromise}>
        {globalState.showSubModal && <SubModal />}
        {globalState.showAddCardModal && <AddCardModal />}
      </Elements>
      {globalState.showAuthModal && <AuthModal />}
      {globalState.showClaimFundsModal && <ClaimFundsModal />}
    </PageContainer>
  );
};

export default DogProfilePage;
